/**
 * Always-visible "Saved databook" indicator. Sits below the VDR folder
 * selector on the project page, above the tab navigation, so the analyst
 * always knows which databook is the canonical source of truth for the
 * current project no matter which tab they're on.
 *
 * Polls the canonical store's status endpoint when mounted and after a
 * build completes (the caller bumps `refreshKey` to force a re-fetch).
 */

import React, { useEffect, useState } from "react";
import { apiFetch } from "../api/fddClient";

type Props = {
  projectId: string;
  /**
   * Optional — when the parent knows a build just finished, bumping this
   * value triggers a re-fetch so the indicator updates without a manual
   * page refresh.
   */
  refreshKey?: number;
};

type Status = {
  exists: boolean;
  builtAt: string | null;
  sourceFilename: string | null;
};

const DatabookStatusIndicator: React.FC<Props> = ({ projectId, refreshKey }) => {
  const [status, setStatus] = useState<Status | null>(null);
  const [loading, setLoading] = useState(true);

  const [tick, setTick] = useState(0);

  // Fetch on mount, when projectId changes, when the parent bumps
  // refreshKey, and whenever any code in the app dispatches a
  // "databook-saved" window event (the Databook builder does this after a
  // successful build, so the indicator updates without a page refresh).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await apiFetch(`/api/databook-store/${encodeURIComponent(projectId)}/status`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as Status;
        if (!cancelled) setStatus(data);
      } catch {
        if (!cancelled) setStatus({ exists: false, builtAt: null, sourceFilename: null });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, refreshKey, tick]);

  // Listen for cross-component "build finished" signals.
  useEffect(() => {
    const onSaved = (ev: Event) => {
      const detail = (ev as CustomEvent).detail || {};
      // If a projectId is provided on the event, only refresh when it
      // matches ours. Otherwise refresh unconditionally.
      if (!detail.projectId || detail.projectId === projectId) {
        setTick((n) => n + 1);
      }
    };
    window.addEventListener("databook-saved", onSaved);
    return () => window.removeEventListener("databook-saved", onSaved);
  }, [projectId]);

  // Manual upload affordance — analyst can drop a pre-built databook into
  // the canonical store without going through the builder.
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("databook", file);
      const res = await apiFetch(
        `/api/databook-store/${encodeURIComponent(projectId)}/upload`,
        { method: "POST", body: formData },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err?.error || `Upload failed (HTTP ${res.status})`);
        return;
      }
      // Bump the local tick AND broadcast so any other listener (e.g. the
      // Commentary panel) refreshes too.
      setTick((n) => n + 1);
      window.dispatchEvent(new CustomEvent("databook-saved", { detail: { projectId } }));
    } finally {
      setUploading(false);
    }
  };
  const onPickFile: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const f = e.target.files?.[0];
    if (f) void handleUpload(f);
    e.target.value = "";
  };

  const hiddenInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept=".xlsx,.xlsm"
      onChange={onPickFile}
      style={{ display: "none" }}
    />
  );

  const uploadButton = (
    <button
      onClick={() => fileInputRef.current?.click()}
      disabled={uploading}
      style={iconBtnStyle(uploading)}
      title="Upload an existing databook"
    >
      {uploading ? "Uploading…" : "⬆ Upload"}
    </button>
  );

  if (loading && !status) {
    return (
      <Bar tone="muted">
        <span>Checking saved databook…</span>
        {hiddenInput}
        {uploadButton}
      </Bar>
    );
  }
  if (!status || !status.exists) {
    return (
      <Bar tone="empty">
        <span>
          <span style={{ marginRight: 8 }}>📭</span>
          No databook saved for this project yet. Build one from the <strong>FDD Toolbox</strong> → <strong>Databook</strong>, or upload one you've already built.
        </span>
        {hiddenInput}
        {uploadButton}
      </Bar>
    );
  }
  return (
    <Bar tone="ok">
      <span>
        <span style={{ marginRight: 8 }}>📊</span>
        <strong>Databook saved</strong>
        {status.sourceFilename ? ` · source: ${status.sourceFilename}` : ""}
        {status.builtAt ? ` · built ${formatRelative(status.builtAt)}` : ""}
      </span>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <a
          href={`/api/databook-store/${encodeURIComponent(projectId)}/databook`}
          style={linkStyle}
          title="Download the saved databook"
        >
          ⬇ Download
        </a>
        {hiddenInput}
        {uploadButton}
      </div>
    </Bar>
  );
};

/* ─────────────────────────── helpers ─────────────────────────── */

function formatRelative(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const now = Date.now();
  const diffMs = now - date.getTime();
  const mins = Math.round(diffMs / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  // Older than a day — show the date.
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/* ─────────────────────────── styling ───────────────────────────── */

type Tone = "ok" | "empty" | "muted";

const TONE_STYLES: Record<Tone, React.CSSProperties> = {
  ok: {
    background: "#f4fbf1",
    border: "1px solid #cfe6c7",
    color: "#1f5e1f",
  },
  empty: {
    background: "#fffbeb",
    border: "1px solid #fde68a",
    color: "#78350f",
  },
  muted: {
    background: "#f9fafb",
    border: "1px solid #e5e7eb",
    color: "#6b7280",
  },
};

const Bar: React.FC<{ tone: Tone; children: React.ReactNode }> = ({ tone, children }) => (
  <div
    style={{
      ...TONE_STYLES[tone],
      borderRadius: 8,
      padding: "8px 12px",
      fontSize: 12,
      margin: "8px 0",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
    }}
  >
    {children}
  </div>
);

const linkStyle: React.CSSProperties = {
  color: "#15803d",
  textDecoration: "none",
  fontWeight: 600,
  fontSize: 11,
};

function iconBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    background: "transparent",
    color: disabled ? "#9ca3af" : "#15803d",
    border: `1px solid ${disabled ? "#d1d5db" : "#cfe6c7"}`,
    borderRadius: 4,
    padding: "2px 8px",
    fontSize: 11,
    fontWeight: 600,
    cursor: disabled ? "not-allowed" : "pointer",
  };
}

export default DatabookStatusIndicator;
